import {Fade, Slide} from '@aboutyou/transitions';
import {styled} from '@linaria/react';
import React, {FC, SyntheticEvent, useCallback} from 'react';
import {createPortal} from 'react-dom';
import {Z_INDEXES} from '../../../constants/layers';
import {useKeyPress} from '../../../hooks/useKeyPress/useKeyPress';
import {useLockBodyScroll} from '../../../hooks/useLockBodyScroll/useLockBodyScroll';
import {useModalPortalElement} from '../../../hooks/usePortalElement/useModalPortalElement';
import {BlurredOverlay} from '../BlurredOverlay/BlurredOverlay';
import {HapticButton} from '../Buttons/HapticButton';
import {ContentPopperBaseProps} from '../ContentPopper/ContentPopper';
import {Icon} from '../Icon/Icon';
import {GenericIconName} from '../Icon/IconName';

export type ModalProps = {
  bodyContent?: ContentPopperBaseProps['bodyContent'];
  onClose: ContentPopperBaseProps['onClose'];
  open: ContentPopperBaseProps['open'];
  onModalClick?: VoidFunction | React.MouseEventHandler;
  showCloseIcon?: boolean;
  dataTestId?: string;
  className?: string;
  /**
   * use `renderInPortal` flag to render the modal in a separate portal element
   * This would be needed in some rare case where the rendered modal has z-index issues
   */
  renderInPortal?: boolean;
  onUnmount?: VoidFunction;
  animated?: boolean;
  zIndex?: Z_INDEXES;
  backgroundBlur?: boolean;
  preventSlideUpAnimation?: boolean;
};

const disableOtherEvents = (e: SyntheticEvent) => {
  e.stopPropagation();
  e.preventDefault();
};

export const Modal: FC<ModalProps> = ({
  bodyContent,
  showCloseIcon = true,
  onClose,
  onModalClick,
  renderInPortal = false,
  dataTestId = 'modal',
  open,
  onUnmount,
  animated = true,
  backgroundBlur = false,
  zIndex = Z_INDEXES.MODAL_DIALOG_WRAPPER,
  preventSlideUpAnimation,
  ...rest
}) => {
  useKeyPress({key: 'Escape', onKeyDown: onClose, disabled: !open});
  useLockBodyScroll({lockScroll: open});

  const onCloseModal = useCallback(
    (e: SyntheticEvent) => {
      disableOtherEvents(e);
      onClose?.();
    },
    [onClose],
  );

  const body =
    animated && !preventSlideUpAnimation ? (
      <Slide mounted={open} direction="up" duration={300} transformLength={5}>
        {bodyContent}
      </Slide>
    ) : (
      bodyContent
    );

  const main = (
    <BlurredOverlay
      onClick={onModalClick}
      onScroll={disableOtherEvents}
      aria-describedby="modalContent"
      aria-modal="true"
      data-testid={dataTestId}
      role="dialog"
      tabIndex={-1}
      $zIndex={zIndex}
      $enableBlur={backgroundBlur}
      {...rest}
    >
      {body}
      {showCloseIcon && (
        <StyledButton
          data-testid={`${dataTestId}CloseIcon`}
          onClick={onCloseModal}
        >
          <Icon asset={GenericIconName.Close} size="M" />
        </StyledButton>
      )}
    </BlurredOverlay>
  );

  let content = open ? main : null;

  if (animated) {
    content = (
      <Fade mounted={open} duration={300} onUnmount={onUnmount}>
        {main}
      </Fade>
    );
  }

  if (renderInPortal) {
    return <RenderInPortal>{content}</RenderInPortal>;
  }

  return content;
};

const RenderInPortal: React.FC<React.PropsWithChildren> = ({children}) => {
  const modalPortalElement = useModalPortalElement();

  if (!modalPortalElement) {
    return null;
  }

  return createPortal(<>{children}</>, modalPortalElement);
};

const StyledButton = styled(HapticButton)`
  position: absolute;
  top: 0;
  right: 0;
  padding: 24px;
`;
